import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { userActions } from '@/redux/actions';
import { userSelectors } from '@/redux/selectors';
import { useInputText } from '@/hooks';
import { ForgotPasswordEmailRequest } from '@/api';
import { Validation } from '@/models';
import parse from 'html-react-parser';
import { EMAIL_REGEX } from '@/config';

import { AlertBox, AuthContainer, Button, Input } from '@/components';

interface PasswordForgotProps {}

const PasswordForgot: FC<PasswordForgotProps> = () => {
  const { t } = useTranslation();

  const { error, loading } = useAppSelector(userSelectors.getUserSlice);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(userActions.setError(null));
  }, []);

  const EMAIL_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'email',
        value: EMAIL_REGEX,
        message: t('translation.invalid_email'),
      },
    ],
    [t],
  );

  const emailField = useInputText('', EMAIL_VALIDATIONS);

  const submitHandler = () => {
    if (!loading) {
      if (emailField.isValid) {
        const payload: ForgotPasswordEmailRequest = {
          emailAddress: emailField.value,
        };

        dispatch(userActions.forgotPasswordEmail(payload));
      } else if (!emailField.isValid) {
        emailField.onBlur();
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>AMLBot - {t('translation.forgot_your_password')}</title>
      </Helmet>
      <AuthContainer
        title={t('translation.forgot_your_password')}
        note={t('translation.send_instructions_to_specified_email')}
        description={
          <Link to="/signin" className="link">
            {t('translation.back_to_login')}
          </Link>
        }
        alert={
          !!error && (
            <AlertBox type="danger">
              {{
                title: parse(t('translation.ooops')),
                description: error,
                icon: '🤔',
              }}
            </AlertBox>
          )
        }
        fields={
          <div>
            <Input
              id="email"
              name="email"
              type="text"
              value={emailField.value}
              onChange={emailField.onChange}
              onBlur={emailField.onBlur}
              isError={emailField.isDirty && !emailField.isValid}
              placeholder={t('translation.email') || ''}
              disabled={loading}
              required
              floating
            />
            {emailField.isDirty && !emailField.isValid && (
              <AlertBox type="danger" small>
                {{
                  description: emailField.errors[0],
                }}
              </AlertBox>
            )}
          </div>
        }
        buttons={
          <Button
            size="xl"
            corner="rect"
            onClick={submitHandler}
            isLoading={loading}
            fullwidth
          >
            {t('translation.submit')}
          </Button>
        }
      />
    </>
  );
};

export default PasswordForgot;
